import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWalletSafe } from "./useWalletSafe";
import { supabaseQueryWithRetry } from "@/lib/retryUtils";

export interface PlatformStats {
  total_nfts: number;
  listed_nfts: number;
  total_users: number;
  banned_users: number;
  total_sales: number;
  total_volume: number;
  pending_withdrawals: number;
  total_withdrawn: number;
  nfts_24h: number;
  users_24h: number;
  sales_24h: number;
}

export interface NFTAnalytics {
  id: string;
  name: string;
  image_url: string | null;
  owner_wallet: string;
  owner_name: string | null;
  price: number;
  current_price: number | null;
  sales_count: number;
  total_earned: number;
  is_listed: boolean | null;
  image_verified: boolean | null;
  created_at: string | null;
  display_price: number;
  avg_sale_price: number;
}

export interface UserAnalytics {
  user_id: string;
  wallet_address: string;
  display_name: string | null;
  avatar_url: string | null;
  role: string;
  is_banned: boolean | null;
  joined_at: string;
  total_earned: number;
  withdrawn: number;
  available_balance: number;
  nfts_owned: number;
  total_nft_sales: number;
  total_nft_earned: number;
  total_purchases?: number;
}

export interface TrendingNFT {
  id: string;
  name: string;
  image_url: string | null;
  owner_wallet: string;
  owner_name: string | null;
  price: number;
  current_price: number | null;
  sales_count: number;
  total_earned: number;
  trending_score: number;
}

export interface DailyStats {
  date: string;
  nfts_created: number;
}

export interface UserDailyStats {
  date: string;
  sales: number;
  purchases: number;
  earned: number;
}

export function useAnalytics(isAdmin: boolean = false) {
  const { publicKey } = useWalletSafe();
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [nftAnalytics, setNftAnalytics] = useState<NFTAnalytics[]>([]);
  const [userAnalytics, setUserAnalytics] = useState<UserAnalytics[]>([]);
  const [trendingNFTs, setTrendingNFTs] = useState<TrendingNFT[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [myAnalytics, setMyAnalytics] = useState<UserAnalytics | null>(null);
  const [myNFTs, setMyNFTs] = useState<NFTAnalytics[]>([]);
  const [myDailyStats, setMyDailyStats] = useState<UserDailyStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlatformStats = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      const { data, error: nftsError } = await supabaseQueryWithRetry(async () =>
        supabase
          .from("nfts")
          .select("id, sales_count, total_earned, is_listed, created_at")
      );
      
      if (nftsError) {
        console.error("Error fetching NFTs for platform stats:", nftsError);
        return;
      }

      const { data: usersData, error: usersError } = await supabaseQueryWithRetry(async () =>
        supabase
          .from("profiles")
          .select("id, is_banned, created_at")
      );
      
      if (usersError) {
        console.error("Error fetching profiles for platform stats:", usersError);
      }

      const { data: withdrawalsData, error: withdrawalsError } = await supabaseQueryWithRetry(async () =>
        supabase
          .from("withdrawal_requests")
          .select("id, status, amount")
      );

      if (withdrawalsError) {
        console.error("Error fetching withdrawals for platform stats:", withdrawalsError);
      }

      const now = new Date();
      const h24Ago = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      const stats: PlatformStats = {
        total_nfts: data?.length || 0,
        listed_nfts: data?.filter(n => n.is_listed).length || 0,
        total_users: usersData?.length || 0,
        banned_users: usersData?.filter(u => u.is_banned).length || 0,
        total_sales: data?.reduce((sum, n) => sum + (n.sales_count || 0), 0) || 0,
        total_volume: data?.reduce((sum, n) => sum + (n.total_earned || 0), 0) || 0,
        pending_withdrawals: withdrawalsData?.filter(w => w.status === 'pending').length || 0,
        total_withdrawn: withdrawalsData?.filter(w => w.status === 'processed').reduce((sum, w) => sum + w.amount, 0) || 0,
        nfts_24h: data?.filter(n => n.created_at && new Date(n.created_at) > h24Ago).length || 0,
        users_24h: usersData?.filter(u => new Date(u.created_at) > h24Ago).length || 0,
        sales_24h: 0,
      };
      setPlatformStats(stats);
    } catch (err) {
      console.error("Unexpected error in fetchPlatformStats:", err);
    }
  }, [isAdmin]);

  const fetchNFTAnalytics = useCallback(async () => {
    const { data, error } = await supabaseQueryWithRetry(async () =>
      supabase
        .from("nfts")
        .select("id, name, image_url, owner_wallet, owner_name, price, current_price, sales_count, total_earned, is_listed, image_verified, created_at")
        .order("total_earned", { ascending: false })
    );

    if (error) {
      console.error("Error fetching NFT analytics:", error);
      return;
    }

    const analytics: NFTAnalytics[] = (data || []).map(n => ({
      ...n,
      display_price: n.current_price || n.price,
      avg_sale_price: n.sales_count > 0 ? n.total_earned / n.sales_count : 0,
    }));

    setNftAnalytics(analytics);
  }, []);

  const fetchUserAnalytics = useCallback(async () => {
    if (!isAdmin) return;

    const { data: profiles, error } = await supabaseQueryWithRetry(async () =>
      supabase
        .from("profiles")
        .select("id, wallet_address, display_name, avatar_url, role, is_banned, created_at")
    );

    if (error) {
      console.error("Error fetching user analytics:", error);
      return;
    }

    const { data: balances } = await supabaseQueryWithRetry(async () =>
      supabase
        .from("user_balances")
        .select("user_id, total_earned, withdrawn, available")
    );

    const { data: nfts } = await supabaseQueryWithRetry(async () =>
      supabase
        .from("nfts")
        .select("owner_wallet, sales_count, total_earned")
    );

    const analytics: UserAnalytics[] = (profiles || []).map(p => {
      const balance = balances?.find(b => b.user_id === p.id);
      const userNfts = nfts?.filter(n => n.owner_wallet === p.wallet_address) || [];
      
      return {
        user_id: p.id,
        wallet_address: p.wallet_address,
        display_name: p.display_name,
        avatar_url: p.avatar_url,
        role: p.role,
        is_banned: p.is_banned,
        joined_at: p.created_at,
        total_earned: balance?.total_earned || 0,
        withdrawn: balance?.withdrawn || 0,
        available_balance: balance?.available || 0,
        nfts_owned: userNfts.length,
        total_nft_sales: userNfts.reduce((sum, n) => sum + (n.sales_count || 0), 0),
        total_nft_earned: userNfts.reduce((sum, n) => sum + (n.total_earned || 0), 0),
      };
    });

    setUserAnalytics(analytics);
  }, [isAdmin]);

  const fetchTrendingNFTs = useCallback(async () => {
    const { data, error } = await supabaseQueryWithRetry(async () =>
      supabase
        .from("nfts")
        .select("id, name, image_url, owner_wallet, owner_name, price, current_price, sales_count, total_earned, is_listed")
        .eq("is_listed", true)
        .eq("image_verified", true)
        .order("total_earned", { ascending: false })
        .limit(10)
    );

    if (error) {
      console.error("Error fetching trending NFTs:", error);
      return;
    }

    const trending: TrendingNFT[] = (data || []).map(n => ({
      ...n,
      trending_score: (n.sales_count * 10) + (n.total_earned * 100),
    }));

    setTrendingNFTs(trending);
  }, []);

  const fetchDailyStats = useCallback(async () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data, error } = await supabaseQueryWithRetry(async () =>
      supabase
        .from("nfts")
        .select("created_at")
        .gte("created_at", thirtyDaysAgo.toISOString())
    );

    if (error) {
      console.error("Error fetching daily stats:", error);
      return;
    }

    const grouped: Record<string, number> = {};
    (data || []).forEach(n => {
      if (n.created_at) {
        const date = n.created_at.split("T")[0];
        grouped[date] = (grouped[date] || 0) + 1;
      }
    });

    const stats: DailyStats[] = Object.entries(grouped)
      .map(([date, count]) => ({ date, nfts_created: count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    setDailyStats(stats);
  }, []);

  const fetchMyAnalytics = useCallback(async () => {
    if (!publicKey) return;
    
    const walletAddress = publicKey.toBase58();

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return;

    // Use direct query for maybeSingle() as the wrapper doesn't handle it well
    const { data: profile, error: profileError } = await supabase
      .from("profiles")
      .select("id, wallet_address, display_name, avatar_url, role, is_banned, created_at")
      .eq("id", session.user.id)
      .maybeSingle();

    if (profileError) {
      console.error("Error fetching my profile:", profileError);
      return;
    }

    if (!profile) return;

    const { data: balance } = await supabase
      .from("user_balances")
      .select("total_earned, withdrawn, available")
      .eq("user_id", profile.id)
      .maybeSingle();

    const { data: nfts } = await supabaseQueryWithRetry(async () =>
      supabase
        .from("nfts")
        .select("id, name, image_url, owner_wallet, owner_name, price, current_price, sales_count, total_earned, is_listed, image_verified, created_at")
        .eq("owner_wallet", walletAddress)
        .order("total_earned", { ascending: false })
    );

    const myNftAnalytics: NFTAnalytics[] = (nfts || []).map(n => ({
      ...n,
      display_price: n.current_price || n.price,
      avg_sale_price: n.sales_count > 0 ? n.total_earned / n.sales_count : 0,
    }));

    setMyNFTs(myNftAnalytics);

    // Fetch user's transaction history to calculate personal sales/purchases
    const { data: txHistory } = await supabaseQueryWithRetry(async () =>
      supabase
        .from("nft_ownership_history")
        .select("created_at, price, from_wallet, to_wallet, transaction_type")
        .or(`from_wallet.eq.${walletAddress},to_wallet.eq.${walletAddress}`)
    );

    // Count personal sales (where user was the seller) and purchases (where user was the buyer)
    let personalSalesCount = 0;
    let personalPurchasesCount = 0;
    let personalEarnings = 0;

    (txHistory || []).forEach(tx => {
      if (tx.transaction_type === 'sale' || tx.transaction_type === 'transfer') {
        if (tx.from_wallet === walletAddress) {
          // User sold this NFT
          personalSalesCount += 1;
          personalEarnings += tx.price || 0;
        } else if (tx.to_wallet === walletAddress && tx.from_wallet !== null) {
          // User bought this NFT (from_wallet !== null means it's not a mint)
          personalPurchasesCount += 1;
        }
      }
    });

    setMyAnalytics({
      user_id: profile.id,
      wallet_address: profile.wallet_address,
      display_name: profile.display_name,
      avatar_url: profile.avatar_url,
      role: profile.role,
      is_banned: profile.is_banned,
      joined_at: profile.created_at,
      total_earned: balance?.total_earned || 0,
      withdrawn: balance?.withdrawn || 0,
      available_balance: balance?.available || 0,
      nfts_owned: myNftAnalytics.length,
      total_nft_sales: personalSalesCount,
      total_nft_earned: personalEarnings,
      total_purchases: personalPurchasesCount,
    } as UserAnalytics & { total_purchases: number });

    // Fetch user's sales history for personal chart
    const { data: salesHistory } = await supabaseQueryWithRetry(async () =>
      supabase
        .from("nft_ownership_history")
        .select("created_at, price, from_wallet, to_wallet")
        .or(`from_wallet.eq.${walletAddress},to_wallet.eq.${walletAddress}`)
        .order("created_at", { ascending: true })
    );

    // Group activity by date
    const groupedActivity: Record<string, { sales: number; purchases: number; earned: number }> = {};
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    (salesHistory || []).forEach(tx => {
      if (tx.created_at) {
        const txDate = new Date(tx.created_at);
        if (txDate >= thirtyDaysAgo) {
          const date = tx.created_at.split("T")[0];
          if (!groupedActivity[date]) {
            groupedActivity[date] = { sales: 0, purchases: 0, earned: 0 };
          }
          if (tx.from_wallet === walletAddress) {
            // User sold
            groupedActivity[date].sales += 1;
            groupedActivity[date].earned += tx.price || 0;
          } else if (tx.to_wallet === walletAddress) {
            // User bought
            groupedActivity[date].purchases += 1;
          }
        }
      }
    });

    // Fill in missing dates with zeros
    const userStats: UserDailyStats[] = [];
    for (let d = new Date(thirtyDaysAgo); d <= new Date(); d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split("T")[0];
      userStats.push({
        date: dateStr,
        sales: groupedActivity[dateStr]?.sales || 0,
        purchases: groupedActivity[dateStr]?.purchases || 0,
        earned: groupedActivity[dateStr]?.earned || 0,
      });
    }

    setMyDailyStats(userStats);
  }, [publicKey]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        fetchPlatformStats(),
        fetchNFTAnalytics(),
        fetchUserAnalytics(),
        fetchTrendingNFTs(),
        fetchDailyStats(),
        fetchMyAnalytics(),
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  }, [fetchPlatformStats, fetchNFTAnalytics, fetchUserAnalytics, fetchTrendingNFTs, fetchDailyStats, fetchMyAnalytics]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const channel = supabase
      .channel("analytics-nfts")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "nfts" },
        () => {
          fetchNFTAnalytics();
          fetchTrendingNFTs();
          fetchDailyStats();
          if (isAdmin) fetchPlatformStats();
          fetchMyAnalytics();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchNFTAnalytics, fetchTrendingNFTs, fetchDailyStats, fetchPlatformStats, fetchMyAnalytics, isAdmin]);

  return {
    platformStats,
    nftAnalytics,
    userAnalytics,
    trendingNFTs,
    dailyStats,
    myAnalytics,
    myNFTs,
    myDailyStats,
    loading,
    error,
    refresh: fetchAll,
  };
}
